import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import toastManager from '../../utils/toastManager';
import { calculateRewardPointsDiscount, calculateCouponDiscount } from '../../utils/helpers';
import { getCoupons, getUserAssignedCoupons } from '../../services/firebase';
import { isPurchasableProduct } from '../../utils/productRules';

const sanitizeCartItems = (items) => {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => {
    if (!item || !item.id) return false;
    // Extension checkout items should remain allowed.
    if ((item.category || '').toLowerCase() === 'extension') return true;
    if ((item.category || '').toLowerCase() === 'script-request' || item.itemType === 'script-request') return true;
    return isPurchasableProduct(item);
  });
};

const loadCartFromStorage = () => {
  if (typeof window === 'undefined') return [];
  try {
    const cartData = localStorage.getItem('psh-cart');
    if (!cartData) return [];

    const parsed = JSON.parse(cartData);
    return sanitizeCartItems(parsed);
  } catch (error) {
    return [];
  }
};

const saveCartToStorage = (cart) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem('psh-cart', JSON.stringify(cart));
  } catch (error) {
    console.warn('LocalStorage access denied', error);
  }
};

const applyRelatedCartItemUpdates = (items, relatedItems = []) => {
  (Array.isArray(relatedItems) ? relatedItems : []).forEach((related) => {
    if (!related?.id || !related?.item) return;
    const relatedIndex = items.findIndex(
      existing => existing.id === related.id && (existing.validity || null) === (related.fromValidity || null)
    );
    if (relatedIndex < 0) return;
    items[relatedIndex] = {
      ...items[relatedIndex],
      ...related.item,
      validity: related.item.validity || null,
      quantity: 1,
      addedAt: items[relatedIndex].addedAt || Date.now(),
      updatedAt: Date.now()
    };
  });
};

export const fetchCoupons = createAsyncThunk(
  'cart/fetchCoupons',
  async (userId = null, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const resolvedUid = userId || state?.auth?.user?.uid || state?.auth?.user?.id || null;
      const [publicCoupons, userCoupons] = await Promise.all([
        getCoupons(),
        resolvedUid ? getUserAssignedCoupons(resolvedUid) : Promise.resolve([])
      ]);

      const map = new Map();
      publicCoupons.forEach(c => map.set(c.code.toUpperCase(), c));
      userCoupons.forEach(c => map.set(c.code.toUpperCase(), { ...c, isUserAssigned: true }));

      return Array.from(map.values());
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    items: loadCartFromStorage(),
    isOpen: false,
    appliedPoints: 0,
    promoCode: null,
    discount: 0,
    appliedCoupon: null,
    coupons: [],
    couponsLoading: false,
    couponError: null,
  },
  reducers: {
    addToCart: (state, action) => {
      const { product, validity, relatedItems = [] } = action.payload;

      const productData = product || action.payload;

      if (!productData?.id) {
        return;
      }

      if (!productData.name && !productData.Name) {
        return;
      }

      if (!isPurchasableProduct(productData)) {
        toastManager.error('This product is not available for purchase');
        return;
      }

      if (typeof productData.price !== 'number' && typeof productData.price !== 'string') {
        return;
      }

      const isAddonItem = Boolean(productData.addonFor || productData.baseProductId || productData.addonId);
      const isExtensionItem = (productData.category || '').toLowerCase() === 'extension' || productData.extensionFor;
      const isScriptRequestItem = (productData.category || '').toLowerCase() === 'script-request' || productData.itemType === 'script-request' || productData.requestId;
      const existingItemIndex = state.items.findIndex((item) => {
        if (isAddonItem || item.addonFor || item.addonId) {
          return item.id === productData.id || (
            item.addonFor === (productData.addonFor || productData.baseProductId || productData.parentProductId) &&
            item.addonId === (productData.addonId || productData.id)
          );
        }
        if (isScriptRequestItem || item.itemType === 'script-request' || item.requestId) {
          return item.id === productData.id || (item.requestId && item.requestId === productData.requestId);
        }
        if (!isExtensionItem && !item.extensionFor && !item.addonFor && !item.addonId) {
          return item.id === productData.id;
        }
        return item.id === productData.id && item.validity === validity;
      });

      if (existingItemIndex >= 0) {
        const existing = state.items[existingItemIndex];
        if (!isAddonItem && !isExtensionItem && (existing.validity || null) !== (validity || null)) {
          state.items[existingItemIndex] = {
            ...existing,
            name: productData.name || productData.Name || existing.name,
            price: productData.price,
            image: productData.image || productData['icon link'] || existing.image || '',
            category: productData.category || existing.category,
            validity: validity || null,
            originalPrice: productData.originalPrice || null,
            storeDiscount: productData.storeDiscount || null,
            updatedAt: Date.now()
          };
          applyRelatedCartItemUpdates(state.items, relatedItems);
          saveCartToStorage(state.items);
          return;
        }
        toastManager.info('Item already in cart');
        return;
      }

      const newItem = {
        id: productData.id,
        name: productData.name || productData.Name,
        price: productData.price,
        image: productData.image || productData['icon link'] || '',
        category: productData.category || 'uncategorized',
        itemType: isScriptRequestItem ? 'script-request' : productData.itemType || null,
        requestId: productData.requestId || null,
        gameName: productData.gameName || null,
        storeLink: productData.storeLink || null,
        note: productData.note || null,
        requestFee: productData.requestFee || null,
        quantity: 1,
        validity: validity || null,
        extensionFor: productData.extensionFor || null,
        renewal: productData.renewal || false,
        extensionDiscountEligible: Boolean(productData.extensionDiscountEligible),
        accessSource: productData.accessSource || null,
        addonFor: productData.addonFor || productData.baseProductId || productData.parentProductId || null,
        addonId: productData.addonId || null,
        addonName: productData.addonName || null,
        baseProductName: productData.baseProductName || productData.parentProductName || null,
        addonPricingSource: productData.addonPricingSource || null,
        addonAdjustedDays: productData.addonAdjustedDays || null,
        addonBaseValidity: productData.addonBaseValidity || null,
        upgradeFromValidity: productData.upgradeFromValidity || null,
        upgradeToValidity: productData.upgradeToValidity || null,
        upgradeDays: productData.upgradeDays || null,
        remainingDays: productData.remainingDays || null,
        originalPrice: productData.originalPrice || null,
        storeDiscount: productData.storeDiscount || null,
        addedAt: Date.now()
      };

      state.items.push(newItem);
      saveCartToStorage(state.items);
    },
    removeFromCart: (state, action) => {
      const { id, validity } = action.payload;
      const uniqueKey = `${id}-${validity || 'default'}`;

      const removedItem = state.items.find(item => `${item.id}-${item.validity || 'default'}` === uniqueKey);
      state.items = state.items.filter(item => {
        const isTarget = `${item.id}-${item.validity || 'default'}` === uniqueKey;
        const isRelatedAddon = removedItem && !removedItem.addonFor && item.addonFor === removedItem.id;
        return !isTarget && !isRelatedAddon;
      });

      // Re-validate coupon min purchase requirements
      if (state.appliedCoupon) {
        const newSubtotal = state.items.reduce((total, item) => {
          const price = typeof item.price === 'string'
            ? parseFloat(item.price.replace(/[^0-9.]/g, ''))
            : item.price;
          return total + price;
        }, 0);

        const minPurchase = Number(state.appliedCoupon.minPurchaseAmount ?? 0) || 0;

        if (minPurchase > 0 && newSubtotal < minPurchase) {
          const currencyLabel = state.appliedCoupon.currency || 'USDT';
          const errorMsg = `Coupon removed: Order must be at least ${minPurchase.toFixed(2)} ${currencyLabel}`;

          state.appliedCoupon = null;
          state.promoCode = null;
          state.discount = 0;

          toastManager.error(errorMsg);
        }
      }

      toastManager.success('Removed from cart');
      saveCartToStorage(state.items);
    },
    updateCartItemValidity: (state, action) => {
      const { id, fromValidity, item, relatedItems = [] } = action.payload || {};
      if (!id || !item?.id) return;

      const sourceIndex = state.items.findIndex(
        existing => existing.id === id && (existing.validity || null) === (fromValidity || null)
      );
      if (sourceIndex < 0) return;

      const targetValidity = item.validity || null;
      const duplicateIndex = state.items.findIndex(
        (existing, index) => index !== sourceIndex && existing.id === item.id && (existing.validity || null) === targetValidity
      );

      const updatedItem = {
        ...state.items[sourceIndex],
        ...item,
        validity: targetValidity,
        quantity: 1,
        addedAt: state.items[sourceIndex].addedAt || Date.now(),
        updatedAt: Date.now()
      };

      if (duplicateIndex >= 0) {
        const existingAddedAt = state.items[duplicateIndex].addedAt;
        state.items[duplicateIndex] = {
          ...state.items[duplicateIndex],
          ...updatedItem,
          addedAt: existingAddedAt || updatedItem.addedAt
        };
        state.items.splice(sourceIndex, 1);
      } else {
        state.items[sourceIndex] = updatedItem;
      }

      applyRelatedCartItemUpdates(state.items, relatedItems);

      if (state.appliedCoupon) {
        const newSubtotal = state.items.reduce((total, cartItem) => {
          const price = typeof cartItem.price === 'string'
            ? parseFloat(cartItem.price.replace(/[^0-9.]/g, ''))
            : cartItem.price;
          return total + price;
        }, 0);

        const minPurchase = Number(state.appliedCoupon.minPurchaseAmount ?? 0) || 0;

        if (minPurchase > 0 && newSubtotal < minPurchase) {
          const currencyLabel = state.appliedCoupon.currency || 'USDT';
          const errorMsg = `Coupon removed: Order must be at least ${minPurchase.toFixed(2)} ${currencyLabel}`;

          state.appliedCoupon = null;
          state.promoCode = null;
          state.discount = 0;

          toastManager.error(errorMsg);
        }
      }

      saveCartToStorage(state.items);
    },

    clearCart: (state) => {
      state.items = [];
      state.appliedPoints = 0;
      state.promoCode = null;
      state.appliedCoupon = null;
      state.discount = 0;
      try {
        localStorage.removeItem('psh-cart');
      } catch (e) {
        console.warn('LocalStorage access denied', e);
      }
      toastManager.success('Cart cleared');
    },
    toggleCart: (state) => {
      state.isOpen = !state.isOpen;
    },
    setCartOpen: (state, action) => {
      state.isOpen = action.payload;
    },
    applyPoints: (state, action) => {
      const pointsToApply = parseInt(action.payload) || 0;

      if (pointsToApply <= 0 || !Number.isInteger(pointsToApply)) {
        state.appliedPoints = 0;
        return;
      }

      state.appliedPoints = Math.max(0, pointsToApply);
    },
    applyPromoCode: (state, action) => {
      const { coupon } = action.payload;
      state.promoCode = coupon.code;
      state.appliedCoupon = coupon;
      state.discount = coupon.type === 'percentage' ? coupon.value : 0;
    },
    removePromoCode: (state) => {
      state.promoCode = null;
      state.appliedCoupon = null;
      state.discount = 0;
      toastManager.success('Promo code removed');
    },
    updatePrices: (state, action) => {
      state.items = sanitizeCartItems(action.payload);

      // Re-validate coupon min purchase requirements
      if (state.appliedCoupon) {
        const newSubtotal = state.items.reduce((total, item) => {
          const price = typeof item.price === 'string'
            ? parseFloat(item.price.replace(/[^0-9.]/g, ''))
            : item.price;
          return total + price;
        }, 0);

        const minPurchase = Number(state.appliedCoupon.minPurchaseAmount ?? 0) || 0;

        if (minPurchase > 0 && newSubtotal < minPurchase) {
          const currencyLabel = state.appliedCoupon.currency || 'USDT';
          const errorMsg = `Coupon removed: Order must be at least ${minPurchase.toFixed(2)} ${currencyLabel}`;

          state.appliedCoupon = null;
          state.promoCode = null;
          state.discount = 0;

          toastManager.error(errorMsg);
        }
      }

      saveCartToStorage(state.items);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCoupons.pending, (state) => {
        state.couponsLoading = true;
        state.couponError = null;
      })
      .addCase(fetchCoupons.fulfilled, (state, action) => {
        state.couponsLoading = false;
        state.coupons = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchCoupons.rejected, (state, action) => {
        state.couponsLoading = false;
        state.couponError = action.payload || 'Failed to load coupons';
      });
  },
});

export const {
  addToCart,
  removeFromCart,
  updateCartItemValidity,
  clearCart,
  toggleCart,
  setCartOpen,
  applyPoints,
  applyPromoCode,
  removePromoCode,
  updatePrices,
} = cartSlice.actions;

export const selectCartItems = (state) => state.cart.items;
export const selectCartTotal = (state) => {
  const subtotal = state.cart.items.reduce((total, item) => {
    const price =
      typeof item.price === 'string'
        ? parseFloat(item.price.replace(/[^0-9.]/g, ''))
        : item.price;
    return total + price;
  }, 0);

  const pointsRate = state.settings?.settings?.pointsRate || 20;
  const pointsDiscount = calculateRewardPointsDiscount(state.cart.appliedPoints, pointsRate);
  const promoDiscount = calculateCouponDiscount(state.cart.appliedCoupon, state.cart.items);

  return Math.max(0, subtotal - pointsDiscount - promoDiscount);
};

export const selectCartItemCount = (state) => state.cart.items.length;

export const selectRewardPointsInfo = (state) => {
  const pointsRate = state.settings?.settings?.pointsRate || 20;
  return {
    appliedPoints: state.cart.appliedPoints,
    discountAmount: calculateRewardPointsDiscount(state.cart.appliedPoints, pointsRate),
    conversionRate: pointsRate,
  };
};

export default cartSlice.reducer;
